import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import api from "@/lib/api";
import { applyTheme } from "@/components/ThemeSwitcher";
import { applyMode } from "@/components/ModeToggle";
import { AuthProvider } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AuthCallback } from "@/components/AuthCallback";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home from "@/pages/Home";
import JusticeBot from "@/pages/JusticeBot";
import CounselAI from "@/pages/CounselAI";
import CaseBrief from "@/pages/CaseBrief";
import VerseTranslation from "@/pages/VerseTranslation";
import PrepMate from "@/pages/PrepMate";
import KnowledgeHub from "@/pages/KnowledgeHub";
import Careers from "@/pages/Careers";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import LandingPage from "@/pages/LandingPage";
import { LANDING_SLUGS } from "@/lib/landingPages";

const Layout = ({ children, hideChrome = false }) => (
  <div className="App min-h-screen grain">
    {!hideChrome && <Navbar />}
    <main className={hideChrome ? "" : "pt-20"}>{children}</main>
    {!hideChrome && <Footer />}
  </div>
);

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Layout><Home /></Layout>} />
      <Route path="/justicebot" element={<Layout><JusticeBot /></Layout>} />
      <Route path="/counsel" element={<Layout><CounselAI /></Layout>} />
      <Route path="/casebrief" element={<Layout><CaseBrief /></Layout>} />
      <Route path="/verse-translation" element={<Layout><VerseTranslation /></Layout>} />
      <Route path="/prepmate" element={<Layout><PrepMate /></Layout>} />
      <Route path="/knowledge-hub" element={<Layout><KnowledgeHub /></Layout>} />
      <Route path="/careers" element={<Layout><Careers /></Layout>} />
      <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
      {LANDING_SLUGS.map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<Layout><LandingPage /></Layout>} />
      ))}
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><Dashboard /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <Layout><AdminDashboard /></Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Apply the saved accent theme as early as possible.
    applyTheme(localStorage.getItem("jv-theme") || "gold");
    applyMode(localStorage.getItem("jv-mode") || "light");
    // Count one visit per page load.
    api.post("/track/visit").catch(() => {});
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" theme="light" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
